import { lazy } from 'react';

// Screens load in separate files that are renamed on every release. A tab that
// was open across a release asks for a file that no longer exists (the server
// answers with the app's HTML, hence "Unexpected token '<'"). Reloading picks
// up the new release. We reload once; a second failure within a minute is a
// real problem, and the error message takes over instead of a reload loop.

const KEY = 'rosa.reloadedForUpdateAt';
const WINDOW_MS = 60 * 1000;

function storage() {
  try {
    const s = window.sessionStorage;
    s.setItem('rosa.storageCheck', '1');
    s.removeItem('rosa.storageCheck');
    return s;
  } catch {
    return null; // private mode or blocked: never reload, we couldn't stop a loop
  }
}

export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk [\w-]+ failed|Unexpected token '<'|Failed to fetch dynamically imported module|Importing a module script failed/i
    .test(String(err.message || ''));
}

/** The loader React.lazy calls, with the reload-once behavior. Exported for tests. */
export function withReload(load, { reload = () => window.location.reload(), now = () => Date.now() } = {}) {
  return () => load().then(
    (mod) => {
      storage()?.removeItem(KEY);
      return mod;
    },
    (err) => {
      const s = storage();
      const last = Number(s?.getItem(KEY)) || 0;
      if (s && isChunkLoadError(err) && now() - last > WINDOW_MS) {
        s.setItem(KEY, String(now()));
        reload();
        return new Promise(() => {}); // keep showing the spinner until the page reloads
      }
      throw err;
    }
  );
}

export function lazyWithReload(load) {
  return lazy(withReload(load));
}
